/**
 * usePageSetupDialog — PDF-6.9a Page Setup Manager orchestration hook.
 *
 * Owns the PAGESETUP / ПАРАМЛИСТ command's modal: lists every layout
 * (model space is intentionally excluded — AutoCAD's PAGESETUP only
 * manages paper-space layouts) and lets the user edit each layout's
 * persisted {@link LayoutPlotSettings}. Per-layout drafts are kept in
 * a `Map<spaceId, PageSetupDraft>` so the user can switch between
 * layouts without losing in-progress edits — only Apply / OK commits
 * dirty drafts back to the model via the store action
 * `setLayoutPlotSettings(spaceId, settings)`.
 *
 * Returned shape mirrors the existing dialog hooks
 * (`usePrintDialog`, `useScaleListEditDialog`):
 *
 *   {
 *     isOpen,
 *     props: {
 *       layouts,            // { spaceId, name }[] — list pane source
 *       drafts,             // current draft map (UI binds via lookup)
 *       activeSpaceId,      // currently selected layout in the list
 *       onSelect,           // change selected layout
 *       onChange,           // update the draft for one layout
 *       onApply,            // persist all dirty drafts (stay open)
 *       onOk,               // Apply + close
 *       onCancel,           // close without persisting
 *     },
 *     open, close,
 *   }
 *
 * AutoCAD parity: PAGESETUP opens the Page Setup Manager, the user
 * selects a layout and edits its page setup (paper, scale, plot-style,
 * rotation, offset, margins, centre / lineweight flags). Apply / OK
 * persists, Cancel discards. The interactive plot-window pick belongs
 * to PRINT/PLOT (PDF-6.7); the manager is the static editor surface.
 */

import { useCallback, useMemo, useState } from "react";

import type { LayoutPlotSettings } from "@dxf-ai-editor/drawing-model";

import {
  pageSetupDraftFromSettings,
  settingsFromPageSetupDraft,
  type PageSetupDraft,
} from "../components/pageSetupBridge";
import { resolvePlotSettings } from "../print/pdf/exportLayoutPdf";
import { useEditorStore } from "../state/editorStore";
import { useSpaceContextStore } from "../state/spaceContextStore";
import { useT } from "../i18n";
import { useDialogOpenState } from "./useDialogOpenState";

/** Minimal layout descriptor exposed to the dialog. */
export interface PageSetupLayoutSummary {
  readonly spaceId: string;
  readonly name: string;
}

export interface UsePageSetupDialogParams {
  /** Receives localized open / apply / cancel feed entries. */
  appendCommandFeed: (text: string) => void;
}

export interface UsePageSetupDialogProps {
  readonly layouts: ReadonlyArray<PageSetupLayoutSummary>;
  readonly drafts: ReadonlyMap<string, PageSetupDraft>;
  readonly activeSpaceId: string | null;
  readonly onSelect: (spaceId: string) => void;
  readonly onChange: (spaceId: string, next: PageSetupDraft) => void;
  readonly onApply: () => void;
  readonly onOk: () => void;
  readonly onCancel: () => void;
}

export interface UsePageSetupDialogResult {
  isOpen: boolean;
  props: UsePageSetupDialogProps;
  open: () => void;
  close: () => void;
}

/**
 * Build the ordered layout list (paper-space layouts only) and the
 * initial draft map. Reads `geometryDocument` non-reactively so the
 * snapshot is the document state at the moment PAGESETUP was invoked.
 */
function buildInitialDrafts(): {
  layouts: PageSetupLayoutSummary[];
  drafts: Map<string, PageSetupDraft>;
} {
  const doc = useEditorStore.getState().geometryDocument;
  const layoutOrder = useSpaceContextStore.getState().layoutOrder;
  const spaceMap = new Map(doc.spaces.map((s) => [s.id, s] as const));
  // layoutOrder drives display order so the manager mirrors the tab
  // strip. Filter to layouts only (Model is not editable in PAGESETUP).
  const ordered: PageSetupLayoutSummary[] = [];
  const seen = new Set<string>();
  for (const id of layoutOrder) {
    const space = spaceMap.get(id);
    if (!space || space.kind !== "layout") continue;
    ordered.push({ spaceId: space.id, name: space.name });
    seen.add(space.id);
  }
  // Append any layouts present in the document but not yet in
  // layoutOrder (newly-added layouts pending a state-context sync).
  for (const space of doc.spaces) {
    if (space.kind !== "layout" || seen.has(space.id)) continue;
    ordered.push({ spaceId: space.id, name: space.name });
  }

  const drafts = new Map<string, PageSetupDraft>();
  for (const layout of ordered) {
    const settings = resolvePlotSettings(doc, layout.spaceId);
    drafts.set(
      layout.spaceId,
      pageSetupDraftFromSettings(settings, layout.spaceId, layout.name),
    );
  }
  return { layouts: ordered, drafts };
}

/**
 * Shallow-compare every editable field of a draft against the layout's
 * currently-persisted plot settings to decide whether Apply needs to
 * write this draft back to the model. We re-project the persisted
 * settings through the same draft mapping so the comparison covers
 * exactly the fields the bridge exposes — no false positives from
 * unrelated flag bits or the preserved window blob.
 */
function isDraftDirty(
  draft: PageSetupDraft,
  persisted: LayoutPlotSettings,
): boolean {
  const baseline = pageSetupDraftFromSettings(
    persisted,
    draft.spaceId,
    draft.name,
  );
  // Compare via JSON: small objects, primitive fields, stable shape.
  return JSON.stringify(draft) !== JSON.stringify(baseline);
}

export function usePageSetupDialog({
  appendCommandFeed,
}: UsePageSetupDialogParams): UsePageSetupDialogResult {
  const t = useT();
  const { isOpen, open: openDialog, close: closeDialog } = useDialogOpenState();
  const [layouts, setLayouts] = useState<PageSetupLayoutSummary[]>([]);
  const [drafts, setDrafts] = useState<Map<string, PageSetupDraft>>(
    () => new Map(),
  );
  const [activeSpaceId, setActiveSpaceId] = useState<string | null>(null);

  const open = useCallback(() => {
    // Snapshot the layout list + initial drafts at the moment the
    // dialog opens. The dialog edits a frozen view of the document;
    // re-opening picks up newly-added layouts.
    const { layouts: nextLayouts, drafts: nextDrafts } = buildInitialDrafts();
    setLayouts(nextLayouts);
    setDrafts(nextDrafts);
    // Default selection: the document's currently-active space if it's
    // a layout, otherwise the first layout. Falls back to null when
    // the document has no layouts (the dialog renders an empty list).
    const docActive = useEditorStore.getState().geometryDocument.activeSpaceId;
    const initialSelection =
      nextLayouts.find((l) => l.spaceId === docActive)?.spaceId ??
      nextLayouts[0]?.spaceId ??
      null;
    setActiveSpaceId(initialSelection);
    openDialog();
    appendCommandFeed(t("page_setup.feed.opened"));
  }, [appendCommandFeed, openDialog, t]);

  const close = useCallback(() => {
    closeDialog();
    // Drop the draft map so a future re-open starts from a clean snapshot.
    setDrafts(new Map());
    setLayouts([]);
    setActiveSpaceId(null);
  }, [closeDialog]);

  const onSelect = useCallback((spaceId: string) => {
    setActiveSpaceId(spaceId);
  }, []);

  const onChange = useCallback(
    (spaceId: string, next: PageSetupDraft) => {
      setDrafts((prev) => {
        if (!prev.has(spaceId)) return prev;
        const updated = new Map(prev);
        updated.set(spaceId, next);
        return updated;
      });
    },
    [],
  );

  /**
   * Persist every dirty draft back to the model via the store action.
   * Returns the count of layouts that were actually written so the
   * feed can report it. Reads `geometryDocument` non-reactively so the
   * dirty check sees the latest persisted state (handles the case
   * where an Apply was already clicked once during this session).
   */
  const applyDirty = useCallback((): number => {
    const store = useEditorStore.getState();
    const doc = store.geometryDocument;
    let writeCount = 0;
    for (const [spaceId, draft] of drafts) {
      const persisted = resolvePlotSettings(doc, spaceId);
      if (!isDraftDirty(draft, persisted)) continue;
      const next = settingsFromPageSetupDraft(draft);
      store.setLayoutPlotSettings(spaceId, next);
      writeCount += 1;
    }
    return writeCount;
  }, [drafts]);

  const onApply = useCallback(() => {
    const count = applyDirty();
    appendCommandFeed(
      t("page_setup.feed.applied", { count: String(count) }),
    );
    // After Apply, every draft becomes the new committed state — refresh
    // the draft map from the freshly-persisted settings so subsequent
    // isDraftDirty checks see them as clean.
    const doc = useEditorStore.getState().geometryDocument;
    setDrafts((prev) => {
      const refreshed = new Map<string, PageSetupDraft>();
      for (const layout of layouts) {
        const persisted = resolvePlotSettings(doc, layout.spaceId);
        const existing = prev.get(layout.spaceId);
        // Re-project the persisted settings; if the draft hadn't been
        // touched and matches, we still rebuild for consistency.
        refreshed.set(
          layout.spaceId,
          pageSetupDraftFromSettings(persisted, layout.spaceId, layout.name),
        );
        // Sanity guard for unused var lint in strict mode.
        void existing;
      }
      return refreshed;
    });
  }, [applyDirty, appendCommandFeed, layouts, t]);

  const onOk = useCallback(() => {
    const count = applyDirty();
    appendCommandFeed(
      t("page_setup.feed.applied", { count: String(count) }),
    );
    close();
  }, [applyDirty, appendCommandFeed, close, t]);

  const onCancel = useCallback(() => {
    appendCommandFeed(t("page_setup.feed.cancelled"));
    close();
  }, [appendCommandFeed, close, t]);

  const props = useMemo<UsePageSetupDialogProps>(
    () => ({
      layouts,
      drafts,
      activeSpaceId,
      onSelect,
      onChange,
      onApply,
      onOk,
      onCancel,
    }),
    [
      layouts,
      drafts,
      activeSpaceId,
      onSelect,
      onChange,
      onApply,
      onOk,
      onCancel,
    ],
  );

  return { isOpen, props, open, close };
}
